import { Injectable } from '@angular/core';
import { BannerNotificationsService, BaseEntity, ExceptionService } from '../../xform-compat';
import { FileType } from '../../shared/constants/file-type';
import { Attachment, Year } from '../../shared/models';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { saveAs } from 'file-saver-es';
import { escape } from 'lodash-es';
import { RmsColors } from '../../shared/constants/rms-colors';

@Injectable({
  providedIn: 'root'
})
export class RmsUtilsService {

  private FILE_TYPES = FileType;

  constructor(
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private http: HttpClient,
    private exceptionService: ExceptionService
  ) { }

  /**
   * Filters an array to return a list of unique items
   * @param {Array<T>} rawArray
   * @returns {Array<T>}
   */
  public static arrayUnique<T>(rawArray: Array<T>): Array<T> {
    return rawArray.filter((elem, pos, arr) => arr.indexOf(elem) === pos);
  }

  /**
   * Allows deep objects copy by value
   * @param source: raw object to assign
   * @returns: new converted object
   */
  public static deepClone (source: any): any {
    return JSON.parse(JSON.stringify(source));
  }

  /**
   * Compares 2 objects to see if they are equal
   * @param x
   * @param y
   */
  public static isEqual (x, y) {
    if (x === y) {
      return true; // if both x and y are null or undefined and exactly the same
    } else if (!(x instanceof Object) || !(y instanceof Object)) {
      return false; // if they are not strictly equal, they both need to be Objects
    } else if (x.constructor !== y.constructor) {
      // they must have the exact same prototype chain, the closest we can do is
      // test their constructor.
      return false;
    } else {
      for (const p in x) {
        if (!x.hasOwnProperty(p)) {
          continue; // other properties were tested using x.constructor === y.constructor
        }
        if (!y.hasOwnProperty(p)) {
          return false; // allows to compare x[ p ] and y[ p ] when set to undefined
        }
        if (x[p] === y[p]) {
          continue; // if they have the same strict value or identity then they are equal
        }
        if (typeof (x[p]) !== 'object') {
          return false; // Numbers, Strings, Functions, Booleans must be strictly equal
        }
        if (!this.isEqual(x[p], y[p])) {
          return false;
        }
      }
      for (const p in y) {
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
          return false;
        }
      }
      return true;
    }
  }

  /**
   * Reset the selected property of any list
   * @param list
   */
  public static resetSelected (list: Array<any>): Array<any> {
    return list.map(item => item.selected = false);
  }

  /**
   * Gets an array and an array key to return it sorted by the provided key
   * @param {Array<T>} arr
   * @param {string} key
   * @returns {Array<T>}
   */
  public static sortByKey<T>(arr: Array<T>, key: string): Array<T> {
    return arr.sort((a, b) => {
      const x = a[key];
      const y = b[key];
      return ((x > y) ? -1 : ((x < y) ? 1 : 0));
    });
  }

  /**
   * Escapes HTML-significant characters so untrusted text can be safely concatenated into an HTML string
   * @param {string} value
   * @returns {string}
   */
  public static escapeHtml(value: string): string {
    return escape(value);
  }

  /**
   * Returns true if user agent correspond to apple browsers
   */
  public static userAgentIOS(): boolean {
    const isIphone = navigator.userAgent.indexOf('iPhone') !== -1 ;
    const isIpod = navigator.userAgent.indexOf('iPod') !== -1 ;
    const isIpad = navigator.userAgent.indexOf('iPad') !== -1 ;
    if (isIphone || isIpod || isIpad) {
      const osIndex = navigator.userAgent.indexOf('OS');
      const version = navigator.userAgent.slice(osIndex + 3, osIndex + 5); // Format "OS [VERSION]"
      if (!Number.isNaN(parseInt(version, 10)) && parseInt(version, 10) > 9) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  /**
   * Filters an array to return a list of unique objects by 'id' property
   * @param {Array<T>} array
   * @returns {Array<T>}
   */
  public arrayUniqueByID<T extends BaseEntity>(array: Array<T>): Array<T> {
    const result = [];
    const map = new Map();
    for (const item of array) {
      if (!map.has(item.id)) {
        map.set(item.id, true);
        result.push(item);
      }
    }
    return result;
  }

  /**
   * Filters an array to return a list of unique objects by the received name property
   * @param {Array<T>} array
   * @param {string} propertyName
   * @returns {Array<T>}
   */
  public arrayUniqueByProperty<T extends BaseEntity>(array: Array<T>, propertyName: string): Array<T> {
    const result = [];
    const map = new Map();
    for (const item of array) {
      if (item && !map.has(item[propertyName])) {
        map.set(item[propertyName], true);
        result.push(item);
      }
    }
    return result;
  }

  /**
   * Copy data to clipboard
   * @param textToCopy
   * @param entity
   */
  public copyToClipboard (textToCopy: string, entity?: string, ): void {
    const el = document.createElement('textarea');
    el.value = textToCopy;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    this.bannerNotificationService.info(`${entity} Copied!`);
  }

  /**
   * Generate a range of years with the current year as base
   * sets a previous and future list of years
   */
  public generateYearList (yearRange = 5): Array<Year> {
    const currentYear = new Date().getFullYear();
    const yearList: Array<Year> = [{selected: false, year: currentYear}];
    for (let i = 1; i <= yearRange; i++) {
      yearList.unshift({selected: false, year: currentYear - i});
      yearList.push({selected: false, year: currentYear + i});
    }
    return yearList;
  }

  /**
   * loads the last navigated page
   */
  public loadLastNavigatedPage(): string {
    let loadLastNavigatedPage = '/dashboard';
    if ( sessionStorage.getItem('lastNavigatedItemSS') ) {
      loadLastNavigatedPage = sessionStorage.getItem('lastNavigatedItemSS');
    } else if ( localStorage.getItem('lastNavigatedItemLS') ) {
      loadLastNavigatedPage = localStorage.getItem('lastNavigatedItemLS');
    }
    return loadLastNavigatedPage;
  }

  /**
   * Assigns an icon to the file type based on its extension
   * @param item
   */
  public setIconType (item: Attachment): {className: string, color: string} {
    const fileType = this.FILE_TYPES[item.fileName.split('.').reverse()[0]];
    if (!fileType) {
      item.fileType = {
        className: 'file-o',
        color: RmsColors.GREY_LIGHT_BLUE
      };
    } else {
      item.fileType = fileType;
    }
    return item.fileType;
  }

  /**
   * Downloads the attachment
   * @param {Attachment} item
   * @param nameProp
   */
  public downloadAttachment(item: Attachment, nameProp?: string): void {
    if (RmsUtilsService.userAgentIOS()) {
      this.bannerNotificationService.error(this.translateService.instant('GENERICS.IPHONE_RESTRICTION'));
      return;
    }
    this.downloadFile(item).subscribe(
      response => {
        const fileName = nameProp ? item[nameProp] : item.fileName;
        saveAs(response, fileName);
        this.bannerNotificationService.success(this.translateService.instant('GENERICS.DOWNLOADING'));
      },
      () => this.bannerNotificationService.error(this.translateService.instant('GENERICS.DOWNLOAD_ERROR'))
    );
  }

  /**
   * Downloads an attachment
   * @param {T} item
   * @returns {Observable<Blob>}
   */
  public downloadFile<T extends BaseEntity>(item: T | Attachment): Observable<Blob> {
    const headers = new HttpHeaders({'Accept': 'application/octet-stream'});
    return this.http.get<T>(`/api/rms/attachments/${item.id}`, {headers, responseType: 'blob' as 'json'}).pipe(
        catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Determines whether the given value is null or undefined
   * @param value The value to check
   */
  public isNotNullOrUndefined(value: any): boolean {
    return value !== null && value !== undefined;
  }

}
